/**
 * Canonical event type registry. Event types follow the `entity.action`
 * convention (CLAUDE.md). This is ATLAS's own set — seeded with the generic
 * notification events (used by notification-worker) plus the Content Factory
 * domain events from the spec. Add entries as services emit new events.
 */
export enum EventType {
	// ─── Notification Events (generic; notification-worker) ────────────────────
	NOTIFICATION_EMAIL_REQUESTED = 'notification.email_requested',
	NOTIFICATION_EMAIL_SENT = 'notification.email_sent',
	NOTIFICATION_EMAIL_FAILED = 'notification.email_failed',

	// ─── File / Asset Events (file-service, file-processing-worker) ────────────
	FILE_UPLOADED = 'file.uploaded',
	FILE_DOWNLOADED = 'file.downloaded',
	FILE_UPDATED = 'file.updated',
	FILE_DELETED = 'file.deleted',
	FILE_REBOUND = 'file.rebound',
	FILE_SHARED = 'file.shared',
	FILE_SHARE_REVOKED = 'file.share_revoked',
	FILE_ACCESS_DENIED = 'file.access_denied',
	FILE_CLEANUP_COMPLETED = 'file.cleanup_completed',
	FILE_PROCESSING_REQUESTED = 'file.processing_requested',
	FILE_PROCESSING_COMPLETED = 'file.processing_completed',
	FILE_PROCESSING_FAILED = 'file.processing_failed',
	// Distinct from FILE_PROCESSING_REQUESTED, which has no subscriber anywhere
	// (an audit signal by omission) — this one IS the reprocess trigger, handled
	// by file-processing-worker's second EventBridge subscription. Re-emitting
	// FILE_UPLOADED instead would re-dispatch every concern matching the file's
	// content type, not just the one the caller asked to retry.
	FILE_REPROCESS_REQUESTED = 'file.reprocess_requested',
	// Published once per upload, when the LAST concern that could still change what the
	// file's text looks like has settled — not when any one extraction finishes. Asking
	// for a summary earlier would describe a PDF from its cheap read while the OCR pass
	// that covers its scanned pages is still running.
	FILE_SUMMARY_REQUESTED = 'file.summary_requested',
	FILE_SUMMARY_GENERATED = 'file.summary_generated',
	FILE_SUMMARY_GENERATION_FAILED = 'file.summary_generation_failed',

	// ─── File Jobs (file-service, file-job-worker) ─────────────────────────────
	// Generic async File Job dispatch, ZIP the only implemented type today. Its
	// own entity (`file_job`, EventEntityType.FILE_JOB below), not folded under
	// `file.*`: a File Job is not itself a File, and it never becomes one.
	FILE_JOB_REQUESTED = 'file_job.requested',
	// ZIP inspection runs on its own queue and worker: an inspection job published
	// as FILE_JOB_REQUESTED would be claimed by the generation worker and failed.
	FILE_JOB_ZIP_INSPECTION_REQUESTED = 'file_job.zip_inspection_requested',
	// Published once per associated `file_job_request` by `FileJobTerminalNotifier`
	// after a COMMITTED terminal transition — never
	// consumed by file-job-worker's own subscription, which only ever consumes
	// FILE_JOB_REQUESTED above, so no self-loop is introduced.
	FILE_JOB_READY = 'file_job.ready',
	FILE_JOB_FAILED = 'file_job.failed',

	// ─── Vault asset imports (external-integration-service) ────────────────────
	// One per import, once no file of it can be retried any more; `data` is the
	// import's record, since no import table exists beside its files.
	VEEVA_VAULT_ASSET_DOWNLOAD_COMPLETED = 'veeva_vault_asset.download_completed',
	VEEVA_VAULT_ASSET_DOWNLOAD_FAILED = 'veeva_vault_asset.download_failed',
	// A queued import request Vault refused for good, so nothing was recorded or downloaded.
	VEEVA_VAULT_ASSET_IMPORT_REFUSED = 'veeva_vault_asset.import_refused',

	// ─── Asset sheet sync (asset-sheet-service) ────────────────────────────────
	ASSET_SHEET_SYNC_COMPLETED = 'asset_sheet.sync_completed',
	// A run, or one tab of it, that changed nothing.
	ASSET_SHEET_SYNC_FAILED = 'asset_sheet.sync_failed',
	// An administrator changed where a column of the sheet goes, on one tab or several.
	ASSET_SHEET_COLUMN_MAP_UPDATED = 'asset_sheet.column_map_updated',

	// ─── Workfront adapter (workfront-adapter-service) ─────────────────────────
	// Configuration changes, audited with the admin who made them.
	WORKFRONT_SCOPE_UPDATED = 'workfront_scope.updated',
	WORKFRONT_CONFIGURED_ID_CREATED = 'workfront_configured_id.created',
	WORKFRONT_CONFIGURED_ID_DELETED = 'workfront_configured_id.deleted',
	// An admin confirmed a resolve proposal: the configured ID now points at another Workfront ID.
	WORKFRONT_CONFIGURED_ID_RESOLVED = 'workfront_configured_id.resolved',
	// An admin asked for an environment check; the Reference worker runs it.
	WORKFRONT_ENVIRONMENT_CHECK_REQUESTED = 'workfront_environment.check_requested',
	// A run's environment check found a configured ID missing, so the run stopped.
	WORKFRONT_ENVIRONMENT_CHECK_FAILED = 'workfront_environment.check_failed',
	// The reference worker's trigger (weekly schedule or an admin's request) and its outcome.
	WORKFRONT_REFERENCE_REFRESH_REQUESTED = 'workfront_reference.refresh_requested',
	WORKFRONT_REFERENCE_REFRESHED = 'workfront_reference.refreshed',
	// A readable error opened, resolved (by the system when its step later succeeded),
	// or ignored by an admin with a reason. Repeats of an open problem emit nothing.
	WORKFRONT_SYNC_ERROR_RAISED = 'workfront_sync_error.raised',
	WORKFRONT_SYNC_ERROR_RESOLVED = 'workfront_sync_error.resolved',
	WORKFRONT_SYNC_ERROR_IGNORED = 'workfront_sync_error.ignored',
	// Discovery on request (`POST /workfront/sync`, mode all); the schedules start it directly.
	WORKFRONT_DISCOVERY_REQUESTED = 'workfront_discovery.requested',
	// A fetch of one bundle. Sent straight to the per-bundle FIFO queue, never on the
	// bus: EventBridge can only give a FIFO target one fixed message group.
	WORKFRONT_BUNDLE_FETCH_REQUESTED = 'workfront_bundle.fetch_requested',
	// An admin's on-demand sync or refetch of one bundle, for the audit log: the request
	// itself travels on the FIFO queue, which the audit log never sees.
	WORKFRONT_BUNDLE_SYNC_REQUESTED = 'workfront_bundle.sync_requested',
	// A fetch's outcome, and the bundle's lifecycle changes it found.
	WORKFRONT_BUNDLE_FETCHED = 'workfront_bundle.fetched',
	WORKFRONT_BUNDLE_CLOSED = 'workfront_bundle.closed',
	WORKFRONT_BUNDLE_REOPENED = 'workfront_bundle.reopened',
	WORKFRONT_BUNDLE_REMOVED = 'workfront_bundle.removed',
	// Map a bundle's stored version again (a publish, a fix, an admin). Like a fetch request,
	// it travels on the per-bundle FIFO queue only, so it never interleaves with a fetch.
	WORKFRONT_BUNDLE_REMAP_REQUESTED = 'workfront_bundle.remap_requested',
	// A bundle version was mapped and its canonical record changed; the apply step's trigger.
	WORKFRONT_BUNDLE_MAPPED = 'workfront_bundle.mapped',
	// A new version of the Workfront mappings was published.
	WORKFRONT_MAPPING_PUBLISHED = 'workfront_mapping.published',
	// An admin discarded the open draft, or ignored an unmapped field.
	WORKFRONT_MAPPING_DRAFT_DISCARDED = 'workfront_mapping.draft_discarded',
	WORKFRONT_MAPPING_FIELD_IGNORED = 'workfront_mapping.field_ignored',
	// An admin's re-map of one bundle, for the audit log: the request itself travels on the FIFO queue.
	WORKFRONT_BUNDLE_REMAP_TRIGGERED = 'workfront_bundle.remap_triggered',
	// An admin moved a discovery stream's cursor.
	WORKFRONT_STREAM_CURSOR_RESET = 'workfront_stream.cursor_reset',

	// ─── Comment Events (comment-service) ──────────────────────────────────────
	COMMENT_CREATED = 'comment.created',
	COMMENT_EDITED = 'comment.edited',
	COMMENT_DELETED = 'comment.deleted',
	// Fans out once per newly-added `metadata.mentions[]` entry (notification routing).
	COMMENT_MENTIONED = 'comment.mentioned',

	// ─── Content Factory domain events (add as the loop is built) ──────────────
	DELIVERABLE_GENERATED = 'deliverable.generated',
	RUN_VALIDATED = 'run.validated',
	CLASSIFICATION_CHANGED = 'classification.changed',

	// ─── Classification core (classification-service) ──────────────────────────
	AXIS_CREATED = 'axis.created',
	AXIS_UPDATED = 'axis.updated',
	AXIS_RETIRED = 'axis.retired',
	AXIS_VALUE_CREATED = 'axis_value.created',
	AXIS_VALUE_UPDATED = 'axis_value.updated',
	AXIS_VALUE_REPARENTED = 'axis_value.reparented',
	AXIS_VALUE_RETIRED = 'axis_value.retired',
	AXIS_VALUE_MERGED = 'axis_value.merged',
	CLASSIFICATION_APPLIED = 'classification.applied',
	CLASSIFICATION_DELETED = 'classification.deleted',
	OBJECT_ATTRIBUTE_SET = 'object_attribute.set',
	VALUE_MAPPING_UPDATED = 'value_mapping.updated',
	VALUE_MAPPING_REMOVED = 'value_mapping.removed',
	VALUE_AVAILABILITY_UPDATED = 'value_availability.updated',

	// ─── Catalog events (classification-service) ───────────────────────────────
	BRAND_CREATED = 'brand.created',
	BRAND_UPDATED = 'brand.updated',
	PRODUCT_CREATED = 'product.created',
	PRODUCT_UPDATED = 'product.updated',

	// ─── User administration (user-service, ADR-0013) ──────────────────────────
	USER_PROVISIONED = 'user.provisioned',
	USER_ROLE_CHANGED = 'user.role_changed',
	USER_JOB_FUNCTION_CHANGED = 'user.job_function_changed',
	USER_STATUS_CHANGED = 'user.status_changed',
	USER_SUPERADMIN_BOOTSTRAPPED = 'user.superadmin_bootstrapped',

	// ─── API keys (user-service, ADR-0013 outbox) ──────────────────────────────
	// The last-used refresh is deliberately NOT an event: it is not a privileged
	// mutation, and one record per authenticated request would flood the outbox.
	API_KEY_CREATED = 'api_key.created',
	API_KEY_REVOKED = 'api_key.revoked',
	// A key the idle sweep took out of service, as opposed to one its owner
	// revoked. Both stop the key working; only the reason distinguishes them.
	API_KEY_BLOCKED = 'api_key.blocked',

	// ─── Work orders (work-order-service) ──────────────────────────────────────
	WORK_ORDER_CREATED = 'work_order.created',
	WORK_ORDER_UPDATED = 'work_order.updated',
	WORK_ORDER_STATUS_CHANGED = 'work_order.status_changed',
	// A transition ATTEMPT that did not commit — no active edge, an unavailable
	// status, an illegal edge, or a forbidden backward attempt (ticket 105).
	// Emitted alongside (never instead of) WORK_ORDER_STATUS_CHANGED, which
	// already covers the committed case.
	WORK_ORDER_TRANSITION_BLOCKED = 'work_order.transition_blocked',
	WORK_ORDER_FILE_ATTACHED = 'work_order.file_attached',
	// The art direction an order's artwork will be generated from was chosen or
	// changed. Audited because it decides what a generation produces, and the
	// generation itself only records the version it used — not who picked it.
	WORK_ORDER_GENERATION_CONTEXT_SAVED = 'work_order.generation_context_saved',
	WORK_ORDER_ARCHIVED = 'work_order.archived',
	// Requested by work-order-service, consumed by the pdf-generator-worker.
	WORK_ORDER_VALIDATION_ASSET_REQUESTED = 'work_order.validation_asset_requested',
	// One checklist item going unchecked → checked. Per ITEM, not per instance:
	// an instance-level "the checklist is done" event would be a different animal
	// and does not exist yet.
	WORK_ORDER_CHECKLIST_ITEM_COMPLETED = 'work_order.checklist_item_completed',
	// Change requests: the reviewer raising one, verifying or un-verifying an
	// item (the verification is what resolves a request), and editing or deleting
	// it. Reopening items on a send-back rides on WORK_ORDER_STATUS_CHANGED.
	WORK_ORDER_CHANGE_REQUEST_RAISED = 'work_order.change_request_raised',
	WORK_ORDER_CHANGE_REQUEST_ITEM_VERIFIED = 'work_order.change_request_item_verified',
	WORK_ORDER_CHANGE_REQUEST_ITEM_UNVERIFIED = 'work_order.change_request_item_unverified',
	WORK_ORDER_CHANGE_REQUEST_ITEM_REMOVED = 'work_order.change_request_item_removed',
	WORK_ORDER_CHANGE_REQUEST_DELETED = 'work_order.change_request_deleted',
	// A reviewer marking or un-marking one deliverable on a review stage.
	WORK_ORDER_DELIVERABLE_REVIEWED = 'work_order.deliverable_reviewed',
	// The Workfront sync (work-order-sync-worker): one work order applied, a whole
	// bundle version done (it drives the adapter, which marks the bundle applied),
	// a work order that could not be written, and an external task's status change.
	WORK_ORDER_SYNC_APPLIED = 'work_order.sync_applied',
	WORK_ORDER_SYNC_COMPLETED = 'work_order_sync.completed',
	WORK_ORDER_SYNC_FAILED = 'work_order.sync_failed',
	WORK_ORDER_EXTERNAL_TASK_CHANGED = 'work_order.external_task_changed',
	// A work order's PM set or changed to another user, by a rule, its parent or a
	// person. Clearing a PM emits nothing.
	WORK_ORDER_PM_ASSIGNED = 'work_order.pm_assigned',

	// ─── Validation-asset generation (pdf-generator-worker) ────────────────────
	VALIDATION_ASSET_READY = 'validation_asset.ready',
	VALIDATION_ASSET_FAILED = 'validation_asset.failed',

	// ─── Rules (rule-service) ───────────────────────────────────────────────────
	RULE_CREATED = 'rule.created',
	RULE_UPDATED = 'rule.updated',
	RULE_RETIRED = 'rule.retired',
	RULE_DELETED = 'rule.deleted',

	// ─── Manual rule validation (rule-validation-worker, ai-router-worker) ─────
	// The action is `rule_validation_requested`, not `validation_requested`: the
	// work-order variant of the latter would sit one word from the existing
	// `work_order.validation_asset_requested`, which requests the Merck-facing
	// verification PDF and means something else entirely.
	FILE_RULE_VALIDATION_REQUESTED = 'file.rule_validation_requested',
	CAMPAIGN_RULE_VALIDATION_REQUESTED = 'campaign.rule_validation_requested',
	WORK_ORDER_RULE_VALIDATION_REQUESTED = 'work_order.rule_validation_requested',
	// One evaluation per (rule, subject) audit record. Never a list of rules: a
	// per-rule retry must not re-judge its siblings.
	RULE_EVALUATION_REQUESTED = 'rule.evaluation_requested',
	RULE_EVALUATED = 'rule.evaluated',
	RULE_EVALUATION_FAILED = 'rule.evaluation_failed',
	VALIDATION_RUN_COMPLETED = 'validation_run.completed',
	VALIDATION_RUN_FAILED = 'validation_run.failed',

	// ─── Campaigns (campaign-service) ──────────────────────────────────────────
	CAMPAIGN_CREATED = 'campaign.created',
	CAMPAIGN_UPDATED = 'campaign.updated',
	CAMPAIGN_TRANSITIONED = 'campaign.transitioned',
	CAMPAIGN_DELETED = 'campaign.deleted',

	// ─── Messages (message-service) ────────────────────────────────────────────
	MESSAGE_CREATED = 'message.created',
	MESSAGE_UPDATED = 'message.updated',
	MESSAGE_DELETED = 'message.deleted',
	// Dispatched to ai-router-worker; results below are audit-trail only (the
	// worker writes back to message-service in-process, not via a consumer here).
	MESSAGE_TRANSLATION_REQUESTED = 'message.translation_requested',
	MESSAGE_TRANSLATED = 'message.translated',
	MESSAGE_TRANSLATION_FAILED = 'message.translation_failed',

	// ─── Articles (article-service) ────────────────────────────────────────────
	ARTICLE_CREATED = 'article.created',
	ARTICLE_UPDATED = 'article.updated',
	ARTICLE_TRANSITIONED = 'article.transitioned',
	ARTICLE_DELETED = 'article.deleted',
	// Dispatched to ai-router-worker's article-translate queue; results below are
	// audit-trail only (the worker writes back to article-service in-process).
	ARTICLE_TRANSLATION_REQUESTED = 'article.translation_requested',
	ARTICLE_TRANSLATED = 'article.translated',
	ARTICLE_TRANSLATION_FAILED = 'article.translation_failed',

	// ─── Image generation (ai-router-worker's image queue) ─────────────────────
	// `image.ping_requested` exercises the provider's credentials and reachability
	// without generating anything — a health check, not a job.
	//
	// Generation and expand are separate because they run at different cadences: a
	// hero is generated ONCE PER MARKET from the visual axes, then outpainted ONCE
	// PER FORMAT to fill each ratio. Conflating them would emit a different animal
	// for every banner size of the same campaign.
	//
	// Each carries ONE format. The reserved-concurrency cap is what bounds paid
	// spend, and a cap only means anything if one invocation is one paid job.
	IMAGE_PING_REQUESTED = 'image.ping_requested',
	IMAGE_GENERATION_REQUESTED = 'image.generation_requested',
	IMAGE_GENERATED = 'image.generated',
	IMAGE_GENERATION_FAILED = 'image.generation_failed',
	IMAGE_EXPAND_REQUESTED = 'image.expand_requested',
	IMAGE_EXPANDED = 'image.expanded',
	IMAGE_EXPANSION_FAILED = 'image.expansion_failed',

	// ─── Knowledge layer (knowledge-indexer-worker) ────────────────────────────
	// The knowledge layer's ONLY input, emitted by whichever service decided its
	// content is ready to index. Command-shaped like `file.reprocess_requested`
	// and `notification.email_requested` above: the emitter states an intent, not
	// a fact about itself. See `knowledge-events.types.ts` for why the coupling
	// runs this direction, and ADR-0022 A27.
	KNOWLEDGE_INDEX_REQUESTED = 'knowledge.index_requested',

	// ─── Inbound webhooks (webhook-service) ────────────────────────────────────
	WEBHOOK_RECEIVED = 'webhook.received',

	// ─── Prompt library (prompt-service) ───────────────────────────────────────
	PROMPT_CREATED = 'prompt.created',
	PROMPT_UPDATED = 'prompt.updated',
	PROMPT_PUBLISHED = 'prompt.published',
	PROMPT_ARCHIVED = 'prompt.archived',
	PROMPT_DELETED = 'prompt.deleted',
	// The single most audit-sensitive action in the library: repointing which
	// version production traffic resolves to.
	PROMPT_LABEL_MOVED = 'prompt.label_moved',
	PROMPT_VARIABLE_LIST_CREATED = 'prompt_variable_list.created',
	PROMPT_VARIABLE_LIST_UPDATED = 'prompt_variable_list.updated',
	PROMPT_VARIABLE_LIST_DELETED = 'prompt_variable_list.deleted',
	PROMPT_DEFAULT_CONFIG_UPDATED = 'prompt_default_config.updated',

	// Eval dispatch → ai-router. A DISTINCT type from MESSAGE_TRANSLATION_REQUESTED
	// on purpose: event type is what selects the worker's execution path, so
	// overloading translate's type would make the payload contract ambiguous.
	PROMPT_EVAL_REQUESTED = 'prompt.eval_requested',
	PROMPT_EVAL_COMPLETED = 'prompt.eval_completed',
	PROMPT_EVAL_FAILED = 'prompt.eval_failed',

	// GenAI drafting — asynchronous for the same reason evals are: the provider
	// runs zero retries and depends on the queue's retry policy.
	PROMPT_TEST_CASES_REQUESTED = 'prompt.test_cases_requested',
	PROMPT_TEST_CASES_GENERATED = 'prompt.test_cases_generated',
	// Per-case authoring. A test case is what a publish gate is measured against, so
	// changing one changes what "passing" means — that belongs in the audit log next
	// to the prompt edits themselves.
	PROMPT_TEST_CASE_CREATED = 'prompt.test_case_created',
	PROMPT_TEST_CASE_UPDATED = 'prompt.test_case_updated',
	PROMPT_TEST_CASE_DELETED = 'prompt.test_case_deleted',
	// Tenant-wide library settings, including the eval-blocking flag.
	PROMPT_TENANT_SETTINGS_UPDATED = 'prompt.tenant_settings_updated',
	PROMPT_OUTPUT_SCHEMA_REQUESTED = 'prompt.output_schema_requested',
	PROMPT_OUTPUT_SCHEMA_GENERATED = 'prompt.output_schema_generated',

	// ─── LLM execution audit (ai-router, EVERY use case) ───────────────────────
	// Named `ai_execution.*` rather than `prompt.executed` because it must also
	// cover use cases with no prompt-library prompt (translate). Per-work-order
	// cost only aggregates if every execution emits the same type.
	AI_EXECUTION_COMPLETED = 'ai_execution.completed',
	// Emitted even when the task failed: a failed attempt still burns tokens, and
	// counting only successes would under-report spend.
	AI_EXECUTION_FAILED = 'ai_execution.failed',
}

/**
 * The event types an `emit_event` transition hook may publish (ADR-0024).
 *
 * Empty by design, so every `emit_event` write is rejected and every already
 * seeded row is skipped at execution time. A hook knows only its configured
 * `eventType` string, and every type above carries a typed `data` shape it
 * cannot construct: `work_order.validation_asset_requested` needs a
 * `requestId`/`assetType`/`channel`/`version` that exist only once a request
 * row is written, `work_order.file_attached` needs a `fileId`, the
 * notification types need recipients. The two whose payload IS derivable from
 * a transition (`work_order.updated`, `work_order.archived`) would misreport
 * what happened and wake their real consumers.
 *
 * Add an entry only together with the consumer that needs it.
 * `WORK_ORDER_STATUS_CHANGED` is permanently ineligible: it triggers post-hook
 * execution, so publishing it from a hook would re-trigger that execution.
 */
export const EMIT_EVENT_ALLOWED: readonly EventType[] = []

/**
 * Entity-kind values for event envelopes (`entityRef.type`), the
 * `events.entity_type` column, and the comments primitive's `entityType`.
 * The single source of truth for "what kind of thing does this event/comment
 * belong to?" (ADR-0006).
 */
export enum EventEntityType {
	FILE = 'file',
	COMMENT = 'comment',
	ASSET = 'asset',
	DELIVERABLE = 'deliverable',
	RUN = 'run',
	RULE = 'rule',
	CAMPAIGN = 'campaign',
	CLASSIFICATION = 'classification',
	AXIS = 'axis',
	AXIS_VALUE = 'axis_value',
	VALUE_MAPPING = 'value_mapping',
	BRAND = 'brand',
	PRODUCT = 'product',
	USER = 'user',
	// The key row, not its owner: a user has several keys over time, so an audit
	// trail addressed at the user could not say which one an event was about.
	API_KEY = 'api_key',
	WORK_ORDER = 'work_order',
	WEBHOOK = 'webhook',
	MESSAGE = 'message',
	ARTICLE = 'article',
	// The request row owned by work-order-service.
	VALIDATION_ASSET_REQUEST = 'validation_asset_request',
	// The produced asset the render worker classifies + stores.
	VALIDATION_ASSET = 'validation_asset',
	// The tenant itself — the audited entity when tenant-wide settings change.
	TENANT = 'tenant',
	// Prompt library (prompt-service).
	PROMPT = 'prompt',
	PROMPT_VARIABLE_LIST = 'prompt_variable_list',
	// One LLM execution — the subject of the `ai_execution.*` cost/audit events.
	AI_EXECUTION = 'ai_execution',
	// The shared `file_job` row — not the requester-specific
	// `file_job_request`, which has no audit-log entity kind of its own yet.
	FILE_JOB = 'file_job',
	// A rule-validation run. Distinct from VALIDATION_ASSET_REQUEST above, which
	// requests the verification PDF; this one requests a per-rule compliance report.
	RULE_VALIDATION_REQUEST = 'rule_validation_request',
	// A Vault asset code imported into ATLAS storage, addressed by the asset code.
	VEEVA_VAULT_ASSET = 'veeva_vault_asset',
	// The administrators' asset sheet, addressed by tenant.
	ASSET_SHEET = 'asset_sheet',
	// Workfront adapter configuration: a scope key, a configured ID, and a run.
	WORKFRONT_SCOPE = 'workfront_scope',
	WORKFRONT_CONFIGURED_ID = 'workfront_configured_id',
	WORKFRONT_RUN = 'workfront_run',
	WORKFRONT_SYNC_ERROR = 'workfront_sync_error',
	WORKFRONT_BUNDLE = 'workfront_bundle',
	WORKFRONT_STREAM = 'workfront_stream',
	WORKFRONT_MAPPING = 'workfront_mapping',
}

/**
 * The entity kinds that can COMMISSION an LLM execution — the allowlist for an
 * audit envelope's `businessEntityRef` (ADR-0022).
 *
 * A deliberate subset of `EventEntityType`, not the whole vocabulary. The audit
 * event's `entityRef` is already the prompt, so `PROMPT` as an origin is circular,
 * and `AI_EXECUTION` as its own origin answers nothing; `AXIS`, `WEBHOOK` and the
 * rest never commission work. Accepting them would let a caller store an
 * attribution that reads as populated but aggregates into nothing.
 *
 * Widen this deliberately when a new kind of work can pay for an execution.
 */
export const BUSINESS_ORIGIN_ENTITY_TYPES = [
	EventEntityType.WORK_ORDER,
	EventEntityType.CAMPAIGN,
	EventEntityType.MESSAGE,
	EventEntityType.ARTICLE,
	EventEntityType.DELIVERABLE,
] as const

/** An entity kind permitted to originate an execution. */
export type BusinessOriginEntityType = (typeof BUSINESS_ORIGIN_ENTITY_TYPES)[number]

/**
 * Producer kind for events built through `buildEvent` against the typed
 * `IAuditEvent`/`IFileEvent` envelopes. Values are serialized on the wire and
 * stored in the `events` table, so they must not change.
 */
export enum EventSource {
	API = 'api',
	WORKER = 'worker',
	CRON = 'cron',
}

/**
 * Generic audit-event envelope (ADR-0006). `entityRef` is REQUIRED — the
 * indexed `(entity_type, entity_id)` read is the whole point of an audit event.
 * `buildEvent` populates it; event-service's consumer copies it into the
 * `events` table's indexed columns. Parameterized by event `type` + `data` shape.
 */
export interface IAuditEvent<T extends EventType, D> {
	eventId: string
	type: T
	version: '1.0'
	timestamp: string
	correlationId?: string
	/** Immediate parent's `eventId`; absent on a chain root. */
	causationId?: string
	/** Causal steps from the chain root; absent is read as 0. */
	hopCount?: number
	source: EventSource
	userId?: string
	/** What this event is ABOUT. Required — the indexed read is the point (ADR-0006). */
	entityRef: { type: EventEntityType; id: string }
	/**
	 * What the work was FOR — the originating business entity (a work order,
	 * campaign, message), as opposed to `entityRef`'s subject (ADR-0022).
	 *
	 * Optional, and absent rather than a placeholder when there is no business
	 * origin: a fabricated origin corrupts every aggregate that trusts the column.
	 * Exists because "what did this work order cost?" cannot be answered by
	 * `entityRef` (the prompt) or `correlationId` (one opaque chain).
	 */
	businessEntityRef?: { type: EventEntityType; id: string }
	data: D
}
